import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSearchField,
  AdminListFilterDropdown,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminRowAvatar,
  AdminSupportingText,
  AdminRowActionsButton,
  AdminRowMenuItem,
  ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS,
  AdminRowMenuIcon,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import {
  businessSubscriptions,
  PLAN_FILTER_OPTIONS,
  SUBSCRIPTION_STATUS_OPTIONS,
  LAST_PAYMENT_STATUS_OPTIONS,
  formatSubscriptionDateTime,
  formatPaymentMethod,
  formatMonthlyAmount,
} from './businessSubscriptionsData';

const BillingDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [subscriptions] = useState(businessSubscriptions);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({
    currentPlan: null,
    subscriptionStatus: null,
    lastPaymentStatus: null,
  });
  const [planOpen, setPlanOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [paymentStatusOpen, setPaymentStatusOpen] = useState(false);
  const planRef = useRef(null);
  const statusRef = useRef(null);
  const paymentStatusRef = useRef(null);
  const itemsPerPage = CONFIG_LIST_PAGE_SIZE;
  const [currentPage, setCurrentPage] = useState(1);
  const [rowActionsOpen, setRowActionsOpen] = useState({});
  const rowActionsRefs = useRef({});

  useClickOutside(planRef, () => setPlanOpen(false));
  useClickOutside(statusRef, () => setStatusOpen(false));
  useClickOutside(paymentStatusRef, () => setPaymentStatusOpen(false));

  // Deep-link from Business Detail: pre-fill search with Business ID
  useEffect(() => {
    const businessId = location.state?.businessId;
    if (businessId) {
      setSearchQuery(String(businessId));
      setDebouncedSearch(String(businessId));
    }
  }, [location.state]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(rowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setCurrentPage(1);
  };

  const matchesSearch = (row, term) => {
    if (!term) return true;
    const q = term.toLowerCase();
    return (
      (row.businessName && row.businessName.toLowerCase().includes(q)) ||
      (row.businessId && row.businessId.toLowerCase().includes(q)) ||
      (row.stripeCustomerId && row.stripeCustomerId.toLowerCase().includes(q))
    );
  };

  const filteredSubscriptions = useMemo(() => {
    return subscriptions.filter((row) => {
      if (!matchesSearch(row, debouncedSearch)) return false;
      if (filters.currentPlan && row.currentPlan !== filters.currentPlan) return false;
      if (filters.subscriptionStatus && row.subscriptionStatus !== filters.subscriptionStatus) {
        return false;
      }
      if (filters.lastPaymentStatus && row.lastPaymentStatus !== filters.lastPaymentStatus) {
        return false;
      }
      return true;
    });
  }, [subscriptions, debouncedSearch, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredSubscriptions.length / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [filteredSubscriptions.length, currentPage, totalPages]);

  const paginatedSubscriptions = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredSubscriptions.slice(start, start + itemsPerPage);
  }, [filteredSubscriptions, currentPage, itemsPerPage]);

  const rangeStart =
    filteredSubscriptions.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(currentPage * itemsPerPage, filteredSubscriptions.length);
  const showPagination = filteredSubscriptions.length > itemsPerPage;

  const openSubscriptionDetail = (row) => {
    navigate(`/admin/billing/subscriptions/${encodeURIComponent(row.businessId)}`, {
      state: { from: location.pathname, subscription: row },
    });
  };

  const toggleRowActions = (rowKey) => {
    setRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  // Account ID | Account | Plan | Sub Status | Amount |
  // Last Payment Status | Payment Method | Last Payment Date | Actions
  const gridTemplateColumns =
    'minmax(88px, 0.85fr) minmax(160px, 1.6fr) minmax(64px, 0.55fr) minmax(100px, 0.95fr) minmax(88px, 0.75fr) minmax(110px, 1fr) minmax(120px, 1fr) minmax(100px, 0.9fr) minmax(52px, auto)';

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Billing & Subscriptions</AdminPageTitle>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <AdminSearchField
              className="w-full max-w-[26.25rem]"
              type="text"
              placeholder="Account, Account ID, Stripe Customer ID"
              title="Search by Account, Account ID, or Stripe Customer ID"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              aria-label="Search business subscriptions"
            />

            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Plan"
                value={filters.currentPlan}
                options={[
                  { value: null, label: 'All' },
                  ...PLAN_FILTER_OPTIONS.map((option) => ({ value: option, label: option })),
                ]}
                onChange={(value) => handleFilterChange('currentPlan', value)}
                menuRef={planRef}
                open={planOpen}
                setOpen={setPlanOpen}
              />
              <AdminListFilterDropdown
                label="Sub Status"
                value={filters.subscriptionStatus}
                options={[
                  { value: null, label: 'All' },
                  ...SUBSCRIPTION_STATUS_OPTIONS.map((option) => ({ value: option, label: option })),
                ]}
                onChange={(value) => handleFilterChange('subscriptionStatus', value)}
                menuRef={statusRef}
                open={statusOpen}
                setOpen={setStatusOpen}
              />
              <AdminListFilterDropdown
                label="Last Payment Status"
                value={filters.lastPaymentStatus}
                options={[
                  { value: null, label: 'All' },
                  ...LAST_PAYMENT_STATUS_OPTIONS.map((option) => ({ value: option, label: option })),
                ]}
                onChange={(value) => handleFilterChange('lastPaymentStatus', value)}
                menuRef={paymentStatusRef}
                open={paymentStatusOpen}
                setOpen={setPaymentStatusOpen}
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          {filteredSubscriptions.length === 0 ? (
            <div className="text-center py-10">
              <AdminSupportingText className="text-mediumgray">
                No business subscriptions match your filters.
              </AdminSupportingText>
            </div>
          ) : (
            <>
              <div
                className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4"
                style={{ gridTemplateColumns, width: '100%' }}
              >
                <AdminTableHeader>Account ID</AdminTableHeader>
                <AdminTableHeader>Account</AdminTableHeader>
                <AdminTableHeader>Plan</AdminTableHeader>
                <AdminTableHeader>Sub Status</AdminTableHeader>
                <AdminTableHeader>Amount</AdminTableHeader>
                <AdminTableHeader>Last Payment Status</AdminTableHeader>
                <AdminTableHeader>Payment Method</AdminTableHeader>
                <AdminTableHeader>Last Payment Date</AdminTableHeader>
                <div className="flex items-center justify-center">
                  <AdminTableHeader>Actions</AdminTableHeader>
                </div>
              </div>

              {paginatedSubscriptions.map((row) => {
                return (
                  <div
                    key={row.id}
                    className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 cursor-pointer gap-4 hover:bg-offwhite/60 transition-colors"
                    style={{ gridTemplateColumns, width: '100%' }}
                    onClick={() => openSubscriptionDetail(row)}
                  >
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate">{row.businessId}</AdminTableBody>
                    </div>

                    <div className="flex items-center gap-3 min-w-0">
                      <AdminRowAvatar src={row.businessLogo} alt={row.businessName} />
                      <AdminTableBody className="truncate">{row.businessName}</AdminTableBody>
                    </div>

                    <div className="flex items-center">
                      <AdminTableBody>{row.currentPlan}</AdminTableBody>
                    </div>

                    <div className="flex items-center">
                      <AdminStatusBadge
                        status={row.subscriptionStatus}
                        label={row.subscriptionStatus}
                      />
                    </div>

                    <div className="flex items-center">
                      <AdminTableBody className="tabular-nums">
                        {formatMonthlyAmount(row.monthlyAmount)}
                      </AdminTableBody>
                    </div>

                    <div className="flex items-center">
                      <AdminStatusBadge
                        status={row.lastPaymentStatus}
                        label={row.lastPaymentStatus || '—'}
                      />
                    </div>

                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate">
                        {formatPaymentMethod(row)}
                      </AdminTableBody>
                    </div>

                    <div className="flex items-center">
                      <AdminTableMeta>
                        {formatSubscriptionDateTime(row.lastPaymentDate)}
                      </AdminTableMeta>
                    </div>

                    <div
                      className="flex items-center justify-center relative"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        className="relative inline-flex"
                        ref={(el) => {
                          rowActionsRefs.current[row.id] = el;
                        }}
                      >
                        <AdminRowActionsButton
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowActions(row.id);
                          }}
                          title="Actions"
                          aria-label="Row actions"
                        >
                          <AdminRowMenuIcon />
                        </AdminRowActionsButton>
                        {rowActionsOpen[row.id] && (
                          <div className={ADMIN_INLINE_ROW_ACTIONS_MENU_CLASS}>
                            <AdminRowMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                openSubscriptionDetail(row);
                                setRowActionsOpen({});
                              }}
                            >
                              View
                            </AdminRowMenuItem>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={filteredSubscriptions.length}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default BillingDashboard;
